import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { Card, PrimaryButton, SecondaryButton, ErrorBanner, Spinner } from "./ui";
import { getMeetingJoinInfo } from "../api/poojaApi";

const STATUS_LABELS = {
  pending: "Payment Pending",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

export default function PoojaMeetingJoin({ bookingId, token }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [joinState, setJoinState] = useState({ joining: false, joined: false, error: null });
  const [remoteOnline, setRemoteOnline] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const clientRef = useRef(null);
  const localTracksRef = useRef([]);

  useEffect(() => {
    let cancelled = false;

    getMeetingJoinInfo(bookingId, token)
      .then((res) => {
        if (!cancelled) setState({ loading: false, error: null, data: res.data });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err.message, data: null });
      });

    return () => {
      cancelled = true;
    };
  }, [bookingId, token]);

  useEffect(() => {
    return () => {
      cleanupAgora();
    };
  }, []);

  const cleanupAgora = async () => {
    try {
      localTracksRef.current.forEach((track) => {
        track.stop();
        track.close();
      });
      localTracksRef.current = [];

      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current.removeAllListeners();
        clientRef.current = null;
      }

      if (localVideoRef.current) {
        localVideoRef.current.innerHTML = "";
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.innerHTML = "";
      }
      setRemoteOnline(false);
    } catch (err) {
      // ignore cleanup errors
    }
  };

  const handleUserPublished = async (user, mediaType) => {
    if (!clientRef.current) return;

    await clientRef.current.subscribe(user, mediaType);

    if (mediaType === "video" && user.videoTrack) {
      setRemoteOnline(true);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.innerHTML = "";
        user.videoTrack.play(remoteVideoRef.current);
      }
    }

    if (mediaType === "audio" && user.audioTrack) {
      user.audioTrack.play();
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    if (mediaType === "video") {
      setRemoteOnline(false);
      if (user.videoTrack) {
        user.videoTrack.stop();
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.innerHTML = "";
      }
    }
  };

  const handleUserLeft = () => {
    setRemoteOnline(false);
    if (remoteVideoRef.current) {
      remoteVideoRef.current.innerHTML = "";
    }
  };

  const joinVideoCall = async () => {
    if (!state.data) return;

    setJoinState({ joining: true, joined: false, error: null });

    try {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;
      client.on("user-published", handleUserPublished);
      client.on("user-unpublished", handleUserUnpublished);
      client.on("user-left", handleUserLeft);

      // Join the Agora channel (wildcard token accepts any uid)
      await client.join(
        state.data.agora_app_id,
        state.data.agora_channel,
        state.data.agora_token,
        null
      );

      // Mark joined so video divs are present in the DOM
      setJoinState({ joining: false, joined: true, error: null });

      // If remote user is already connected in the channel, subscribe & play immediately
      for (const remoteUser of client.remoteUsers) {
        if (remoteUser.hasVideo) {
          await client.subscribe(remoteUser, "video");
          if (remoteVideoRef.current && remoteUser.videoTrack) {
            setRemoteOnline(true);
            remoteVideoRef.current.innerHTML = "";
            remoteUser.videoTrack.play(remoteVideoRef.current);
          }
        }
        if (remoteUser.hasAudio) {
          await client.subscribe(remoteUser, "audio");
          remoteUser.audioTrack?.play();
        }
      }

      // Initialize local microphone and camera
      const [microphoneTrack, cameraTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      localTracksRef.current = [microphoneTrack, cameraTrack];

      await client.publish(localTracksRef.current);

      // Play local camera feed into the container
      if (localVideoRef.current && cameraTrack) {
        localVideoRef.current.innerHTML = "";
        cameraTrack.play(localVideoRef.current);
      }

      setTimeout(() => {
        if (localVideoRef.current && cameraTrack && !localVideoRef.current.querySelector("video")) {
          cameraTrack.play(localVideoRef.current);
        }
      }, 150);
    } catch (err) {
      setJoinState({
        joining: false,
        joined: false,
        error: err?.message || "Unable to join the live session.",
      });
      cleanupAgora();
    }
  };

  const handleLeaveCall = async () => {
    await cleanupAgora();
    setJoinState({ joining: false, joined: false, error: null });
  };

  return (
    <div style={styles.page}>
      <Card style={styles.card}>
        <div style={styles.eyebrow}>🎥 Video Call</div>

        {state.loading && (
          <div style={styles.centerBlock}>
            <Spinner />
            <span style={{ marginLeft: 8 }}>Checking your booking…</span>
          </div>
        )}

        {!state.loading && state.error && (
          <>
            <h2 style={styles.heading}>Can't open this link</h2>
            <ErrorBanner message={state.error} />
            <p style={styles.helpText}>
              This can happen if the booking hasn't been paid for yet, or if
              the link has already expired. If you think this is a mistake,
              contact support with your booking ID.
            </p>
            <PrimaryButton onClick={() => (window.location.href = "/")}>Go to Home</PrimaryButton>
          </>
        )}

        {!state.loading && !state.error && state.data && (
          <>
            <h2 style={styles.heading}>{state.data.pooja_title}</h2>
            <div style={styles.statusRow}>
              <span
                style={{
                  ...styles.statusBadge,
                  background: state.data.status === "confirmed" ? "#3f7a4f" : "var(--ink-soft)",
                }}
              >
                {STATUS_LABELS[state.data.status] || state.data.status}
              </span>
            </div>

            <div style={styles.infoBlock}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Booking ID</span>
                <span style={styles.infoValue}>{state.data.booking_id}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Client</span>
                <span style={styles.infoValue}>{state.data.client_phone}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Pandit Ji</span>
                <span style={styles.infoValue}>
                  {state.data.astrologer_phone || "To be assigned"}
                </span>
              </div>
            </div>

            {state.data.status === "confirmed" ? (
              <>
                {!joinState.joined && (
                  <PrimaryButton onClick={joinVideoCall} style={{ width: "100%", marginTop: 8 }}>
                    {joinState.joining ? "Joining…" : "Join Video Call"}
                  </PrimaryButton>
                )}

                {joinState.error && <ErrorBanner message={joinState.error} />}

                {!joinState.joined && !joinState.joining && (
                  <p style={styles.helpText}>
                    Your booking is confirmed and this link is active. Tap the
                    button to join the session from this browser.
                  </p>
                )}

                {joinState.joining && (
                  <div style={styles.centerBlock}>
                    <Spinner />
                    <span style={{ marginLeft: 8 }}>Connecting to the live call…</span>
                  </div>
                )}

                <div style={{ display: joinState.joined ? "block" : "none" }}>
                  <div style={styles.videoLabel}>
                    Your camera <span style={styles.liveBadge}>● Active</span>
                  </div>
                  <div ref={localVideoRef} style={styles.videoBox} />

                  <div style={styles.videoLabel}>
                    Pandit Ji{" "}
                    {remoteOnline ? (
                      <span style={styles.liveBadge}>● Live</span>
                    ) : (
                      <span style={styles.waitingBadge}>⏳ Waiting for pandit ji to join…</span>
                    )}
                  </div>
                  <div style={{ position: "relative", width: "100%", height: 280, borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
                    <div ref={remoteVideoRef} style={styles.videoBox} />
                    {!remoteOnline && (
                      <div style={styles.placeholderOverlay}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🪔</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Waiting for Pandit Ji</div>
                        <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>
                          The call is active. Video will appear here as soon as Pandit Ji turns on camera.
                        </div>
                      </div>
                    )}
                  </div>

                  <p style={styles.helpText}>
                    Both parties are in this private room. Make sure your microphone and camera permissions are allowed.
                  </p>

                  <div style={{ textAlign: "center", marginTop: 16 }}>
                    <SecondaryButton
                      onClick={handleLeaveCall}
                      style={{ background: "#fee2e2", color: "#b91c1c", border: "1px solid #f87171", fontWeight: 600 }}
                    >
                      Leave Call Room
                    </SecondaryButton>
                  </div>
                </div>
              </>
            ) : (
              <p style={styles.helpText}>
                This booking isn't confirmed yet, so the call isn't active.
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    background: "var(--bg, #faf7f2)",
  },
  card: { width: "100%", maxWidth: 640 },
  eyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: "var(--terracotta)",
    marginBottom: 10,
  },
  heading: {
    fontFamily: "var(--font-serif)",
    fontSize: 22,
    fontWeight: 700,
    color: "var(--ink)",
    margin: "0 0 10px",
  },
  centerBlock: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 0",
    color: "var(--ink-soft)",
    fontSize: 14,
  },
  statusRow: { marginBottom: 18 },
  statusBadge: {
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 12px",
    borderRadius: 999,
    textTransform: "uppercase",
  },
  infoBlock: {
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    padding: "12px 14px",
    marginBottom: 20,
    background: "#fff",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    fontSize: 13.5,
    padding: "6px 0",
    borderBottom: "1px solid var(--line)",
  },
  infoLabel: { color: "var(--ink-soft)" },
  infoValue: { color: "var(--ink)", fontWeight: 600, textAlign: "right" },
  helpText: {
    fontSize: 13.5,
    color: "var(--ink-soft)",
    lineHeight: 1.6,
    margin: "8px 0 18px",
  },
  videoLabel: {
    marginTop: 18,
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 700,
    color: "var(--ink)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  liveBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#16a34a",
    background: "#dcfce7",
    padding: "2px 8px",
    borderRadius: 999,
  },
  waitingBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: "#d97706",
    background: "#fef3c7",
    padding: "2px 8px",
    borderRadius: 999,
  },
  videoBox: {
    width: "100%",
    height: 280,
    position: "relative",
    borderRadius: "var(--radius-sm)",
    background: "#170f2e",
    overflow: "hidden",
  },
  placeholderOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(23, 15, 46, 0.85)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    textAlign: "center",
    padding: "16px",
    pointerEvents: "none",
  },
};